"use client";

import dynamic from "next/dynamic";
import { m } from "framer-motion";
import HeroSection from "@/components/common/HeroSection";
import SectionHeader from "@/components/common/SectionHeader";
import Button from "@/components/common/Button";
import { useIsDark } from "@/context/ThemeContext";
import { twMerge } from "tailwind-merge";
import { aboutUsCards } from "@/constants/AboutData";
import Card from "../home/Card";

// Dynamically import components that are below the fold to improve initial load performance
const VisionAndMisson = dynamic(
  () => import("@/components/about/Vision&Mission")
);
const ImageCarousel = dynamic(() => import("@/components/about/ImageCarousel"));

export default function About() {
  const isdark = useIsDark();

  return (
    <main className="mt-6 mx-5 md:mx-10 xl:mx-[100px] 3xl:mx-[150]">
      <div className="flex flex-col items-center">
        <HeroSection
          heading="About Us"
          subHeading="Let's Build What's Next"
          paragraph="We craft innovative engineering and IT solutions for businesses that value performance, precision, and cutting-edge technology"
          headingStyles="text-[45px] mobile:text-[77px] sm:text-[100px] md:text-[110px] tablet:text-[130px] lg:text-[160px] xl:text-[190px] laptop:text-[220px] 3xl:text-[260px] tracking-[0.01em] leading-[100%] text-center opacity-100 "
          paragraphStyles="xl:w-[962px] xl:text-[32px]"
          scrollTarget="vision"
        />
      </div>

      <div id="vision" className="w-full">
        <VisionAndMisson />
      </div>

      <div>
        <SectionHeader
          title={"Who we are"}
          description={
            "Our work is rooted in collaboration, innovation, and a clear understanding of business needs. We take on projects that require attention to detail, thoughtful execution, and a strong sense of ownership."
          }
          wrapperClassName="xl:mt-25 mb-0 px-0"
          descriptionClassName="xl:mt-[50px] text-paragraph w-full"
        />
      </div>

      <div className="grid grid-cols-2 tablet:grid-cols-4 gap-2 mobile:gap-4 sm:gap-6 tablet:gap-8 lg:gap-12 mb-[30px] lg:mb-[60px] xl:mb-6 w-full max-w-[1244px] px-0 sm:px-1 md:px-2 lg:px-3 laptop:px-4 mt-10 md:mt-0 mx-auto">
        {aboutUsCards.map((item) => (
          <div
            key={item.title}
            className="rounded-xl border border-transparent hover:border-[#FF1CF780] hover:shadow-[0_4px_4px_#FF1CF733] transition-all duration-100 ease-in dark:bg-[#FFFFFF0D] bg-[#FFFFFF]"
          >
            <Card
              title={item.title}
              description={item.description}
              titleClassName="text-[16px] mobile:text-[17px] mobile2:text-[18px] md:text-[20px] lg:text-[21px] xl:text-[22px] laptop:text-2xl 3xl:text-[26px] 4xl:text-[27px]  mt-4 xl:mt-[48px]"
              descriptionClassName="text-[10px] mobile:text-[12px] mobile2:text-[13px] sm:text-[14px] md:text-[15px] tablet:text-[16px] lg:text-[17px] xl:text-[18px] 4xl:text-[21px] "
            />
          </div>
        ))}
      </div>

      <div>
        <SectionHeader
          title={"Why work with us"}
          description={
            "Our approach is efficient, flexible, and transparent, built to support projects of all scales and complexities. Whether you’re launching a new platform, scaling an existing system, or validating an early-stage idea, we bring structure, clarity, and quality to every step."
          }
          wrapperClassName="xl:mt-25 mb-0 px-0"
          descriptionClassName="xl:mt-[50px] text-paragraph w-full"
        />
      </div>

      <div className="sm:mt-10 md:mt-0">
        <ImageCarousel />
      </div>

      <div className="flex flex-col items-center mb-10 xl:mb-25">
        <SectionHeader
          title={"Connect With Us"}
          description={
            "Follow us for insights, updates, and a closer look at our work."
          }
          wrapperClassName="mt-70 mobile:mt-50 sm:mt-30 tablet:mt-32 xl:mt-40 3xl:mt-60 xl:mb-[60px] px-0"
          descriptionClassName="xl:mt-[50px] text-paragraph w-full"
        />
        <Button
          label={"Contact Us"}
          href="/contact"
          className={twMerge(
            "xl:w-[120px] xl:h-10",
            isdark ? "" : "bg-[#DF27DF]"
          )}
        />
      </div>
    </main>
  );
}
